import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { MatchEvent } from 'shared/types/match-event';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { generatePeriodsBySettings } from 'shared/utils/generate-periods-by-settings';
import { ObservableField } from 'shared/utils/observer-field';
import { NewReceivedData, ReceivedEvent } from 'modules/core/domain/interfaces/supabase/received-data';
import supabase from '../supabase';

export type ConnectionType = 'CLOSED' | 'SUBSCRIBED' | 'TIMED_OUT' | 'CHANNEL_ERROR';

type TimeState = {
  seconds: number;
  period: number;
};

export type ScoreTeam = { id: number; score: number };
export type Score = [ScoreTeam, ScoreTeam];

export class MatchChronology {
  chanelEvents?: RealtimeChannel;
  connectionStatus: ObservableField<ConnectionType> = new ObservableField<ConnectionType>('CLOSED');
  events: ObservableField<MatchEvent[]> = new ObservableField<MatchEvent[]>([]);
  lastDateUpdate?: Date = undefined;
  activeBoardId: ObservableField<number | null> = new ObservableField<number | null>(null);
  score: ObservableField<Score | null> = new ObservableField<Score | null>(null);
  currentTime: ObservableField<TimeState> = new ObservableField<TimeState>({
    seconds: 0,
    period: 0,
  });
  private isInit = false;
  private settings: ChampionshipSettings | null = null;

  constructor(private supabase: SupabaseClient) {}

  public init(settings: ChampionshipSettings) {
    this.isInit = true;
    this.settings = settings;
  }

  public handleRecordUpdated(data: { new: NewReceivedData; commit_timestamp: string }) {
    console.debug({ event: 'new data received', data });
    const date = new Date(data.commit_timestamp);
    if (this.lastDateUpdate === date) return;
    this.lastDateUpdate = date;

    this.checkEvents(data.new);
    this.checkActiveTemplate(data.new);
    this.checkTime(data.new);
    this.checkScore(data.new);
  }

  public async observeChanges(matchId: number) {
    this.chanelEvents = this.supabase
      .channel(`room${matchId}`)
      .on(
        // @ts-ignore
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'match_chronology',
          filter: `matchId=eq.${matchId}`,
        },
        this.handleRecordUpdated.bind(this),
      )
      .subscribe((status, err) => {
        console.debug({ status });

        if (err) {
          supabase.overlayLogs.createError({ comment: 'Не удалось подключиться к supabase', message: err.toString() });
          console.error({ err });
        }

        this.connectionStatus.value = status;
      }, 10000);
  }

  public async stopObserveChanges() {
    if (this.chanelEvents) {
      return await this.chanelEvents.unsubscribe();
    }
  }

  public async getRow(matchId: number): Promise<NewReceivedData | null> {
    const { data, error } = await this.supabase.from('match_chronology').select().eq('matchId', matchId).single();

    if (!error) return data;
    if (error.code === 'PGRST116') {
      return this.createMatchChronology(matchId);
    }

    console.error(error);
    supabase.overlayLogs.createError({ comment: 'Не удалось получить данные из supabase', message: error.message });
    return null;
  }

  private checkEvents(data: NewReceivedData) {
    const chronology: ReceivedEvent[] = data.chronology;
    this.events.value = chronology.sort((a, b) => a.id - b.id);
  }

  private checkActiveTemplate(data: NewReceivedData) {
    this.activeBoardId.value = data.templateId;
  }

  private checkTime(data: NewReceivedData) {
    const { seconds, currentPeriod: period } = data;
    this.currentTime.value = {
      ...this.currentTime.value,
      seconds,
      period,
    };
  }

  private checkScore(data: NewReceivedData) {
    const { firstTeamScore, firstTeamId, secondTeamId, secondTeamScore } = data;
    this.score.value = [
      { id: firstTeamId, score: firstTeamScore },
      { id: secondTeamId, score: secondTeamScore },
    ];
  }

  private async createMatchChronology(matchId: number) {
    if (!this.isInit || !this.settings) {
      console.trace('try to create match chronology before init');
      throw Error('try to create match chronology before init');
    }

    const periods = generatePeriodsBySettings(this.settings);
    const seconds = this.settings.countdownTimer ? this.settings.gamePeriodsNumber : 0;

    const { data, error } = await this.supabase
      .from('match_chronology')
      .insert({
        matchId,
        periods,
        seconds,
      })
      .select()
      .single();

    if (error) {
      console.error('Error by createMatchChronology:', error);
      throw error;
    }

    return data;
  }
}
